import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  fetchMealsByRestaurant, 
  getMealAvailability, 
  setMealAvailability, 
  removeMealAvailability 
} from "../service/mealService";
import { formatDateForApi } from "../utils/dateUtils";

export const useMealAvailability = (restaurantId) => {
  const [selectedMeals, setSelectedMeals] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [changes, setChanges] = useState(new Set());
  const [viewMode, setViewMode] = useState("manage");
  const [localAvailability, setLocalAvailability] = useState({});
  const previousAvailabilityRef = useRef();
  const queryClient = useQueryClient();

  // Memoize selected meal IDs to prevent unnecessary re-renders
  const selectedMealIds = useMemo(() => selectedMeals.map(m => m.id), [selectedMeals]);

  // Memoize formatDate function - timezone safe
  const formatDate = useMemo(() => formatDateForApi, []);

  // Fetch meals using React Query
  const { 
    data: meals = [], 
    isLoading: mealsLoading, 
    error: mealsError 
  } = useQuery({
    queryKey: ["meals", restaurantId],
    queryFn: () => fetchMealsByRestaurant(restaurantId, 0, 200).then(response => response.content || []),
    enabled: !!restaurantId,
    staleTime: 15 * 1000, // 15 seconds
  });

  // Fetch availability for selected meals using React Query
  const { 
    data: availability = {}, 
    isLoading: availabilityLoading, 
    refetch: refetchAvailability 
  } = useQuery({
    queryKey: ["mealAvailability", restaurantId, selectedMealIds, currentDate.getFullYear(), currentDate.getMonth(), viewMode],
    queryFn: async () => {
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      if (viewMode === "view") {
        // In view mode, get all availabilities for the current month
        const response = await getMealAvailability({
          restaurantId,
          startDate: formatDate(startDate),
          endDate: formatDate(endDate)
        });
        
        // Group by date and mealId
        const availabilityMap = {};
        response.forEach(item => {
          // Ensure date is in string format (YYYY-MM-DD)
          const dateStr = typeof item.date === 'string' ? item.date : item.date.toString();
          if (!availabilityMap[dateStr]) {
            availabilityMap[dateStr] = {};
          }
          availabilityMap[dateStr][item.mealId] = {
            available: true,
            id: item.id,
            mealName: item.mealName
          };
        });
        
        return availabilityMap;
      } else {
        // In manage mode, only get availability for selected meals
        if (selectedMeals.length === 0) return {};
        
        const availabilityMap = {};
        
        // Load availability for all selected meals
        for (const meal of selectedMeals) {
          const response = await getMealAvailability({
            restaurantId,
            mealId: meal.id,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate)
          });

          response.forEach(item => {
            // Ensure date is in string format (YYYY-MM-DD)
            const dateStr = typeof item.date === 'string' ? item.date : item.date.toString();
            if (!availabilityMap[dateStr]) {
              availabilityMap[dateStr] = {};
            }
            availabilityMap[dateStr][meal.id] = {
              available: true,
              id: item.id,
              mealName: item.mealName
            };
          });
        }
        
        return availabilityMap;
      }
    },
    enabled: !!restaurantId && (viewMode === "view" || selectedMeals.length > 0),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Mutation for saving availability changes
  const saveChangesMutation = useMutation({
    mutationFn: async (changesData) => {
      const { changesToAdd, changesToRemove } = changesData;
      
      const promises = [];
      
      // Save additions
      if (changesToAdd.length > 0) {
        promises.push(setMealAvailability(changesToAdd));
      }
      
      // Save removals
      if (changesToRemove.length > 0) {
        promises.push(removeMealAvailability(changesToRemove));
      }
      
      await Promise.all(promises);
    },
    onSuccess: () => {
      setChanges(new Set());
      refetchAvailability();
    },
    onError: (error) => {
      console.error("Failed to save changes:", error);
    },
  });

  // Reset changes when selected meals change
  useEffect(() => {
    setChanges(new Set());
    setLocalAvailability({});
  }, [selectedMealIds]);

  // Update local state when server data changes (only when content actually changes)
  useEffect(() => {
    // Only update if availability has actually changed (deep comparison)
    const availabilityStr = JSON.stringify(availability);
    const previousStr = JSON.stringify(previousAvailabilityRef.current);
    
    if (availabilityStr !== previousStr) {
      previousAvailabilityRef.current = availability;
      setLocalAvailability(availability);
    }
  }, [availability]);

  const toggleAvailability = useCallback((date) => {
    if (selectedMeals.length === 0) return;
    
    const dateStr = formatDate(date);
    
    setLocalAvailability(prev => {
      const newAvailability = { ...prev };
      if (!newAvailability[dateStr]) {
        newAvailability[dateStr] = {};
      }
      
      // Toggle availability for all selected meals on this date
      const allAvailable = selectedMeals.every(meal => newAvailability[dateStr][meal.id]?.available);
      
      selectedMeals.forEach(meal => {
        newAvailability[dateStr][meal.id] = {
          available: !allAvailable,
          id: newAvailability[dateStr][meal.id]?.id, // Keep existing ID if available
          mealName: meal.name
        };
      });
      
      return newAvailability;
    });
    
    setChanges(prev => new Set([...prev, dateStr]));
  }, [selectedMeals, formatDate]);

  const saveChanges = useCallback(async () => {
    if (selectedMeals.length === 0 || changes.size === 0) return;
    
    const changesToAdd = [];
    const changesToRemove = [];
    
    changes.forEach(dateStr => {
      selectedMeals.forEach(meal => {
        const availabilityInfo = localAvailability[dateStr]?.[meal.id];
        const isAvailable = availabilityInfo?.available;
        
        if (isAvailable) {
          // Add new availability
          changesToAdd.push({
            mealId: meal.id,
            date: dateStr,
            restaurantId: restaurantId
          });
        } else if (availabilityInfo?.id) {
          // Remove existing availability using ID
          changesToRemove.push({
            id: availabilityInfo.id,
            mealId: meal.id,
            date: dateStr,
            restaurantId: restaurantId
          });
        }
      });
    });
    
    saveChangesMutation.mutate({ changesToAdd, changesToRemove });
  }, [selectedMeals, changes, localAvailability, restaurantId, saveChangesMutation]);

  const navigateMonth = useCallback((direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
    setChanges(new Set()); // Clear unsaved changes when navigating
  }, [currentDate]);

  const clearChanges = useCallback(() => {
    setChanges(new Set());
  }, []);

  const filteredMeals = useMemo(() => {
    return meals.filter(meal => !meal.isDaily);
  }, [meals]);

  const dailyMeals = useMemo(() => {
    return meals.filter(meal => meal.isDaily);
  }, [meals]);

  return {
    // State
    selectedMeals,
    setSelectedMeals,
    currentDate,
    changes,
    viewMode,
    setViewMode,
    localAvailability,
    
    // Computed values
    selectedMealIds,
    filteredMeals,
    dailyMeals,
    hasChanges: changes.size > 0,
    isLoading: mealsLoading || availabilityLoading,
    isSaving: saveChangesMutation.isPending,
    mealsError,
    
    // Functions
    formatDate,
    toggleAvailability,
    saveChanges,
    navigateMonth,
    clearChanges,
    
    // React Query
    refetchAvailability,
    queryClient
  };
};
